import playgroundApi from "../playgroundApi";
import nativeBridge from "../nativeBridge";
import {userAuthentication} from "../user/authentication";
import {
  isSocialAuthNeedsEmailResponse,
  ISocialAuthNeedsEmailResponse,
} from "../../client/models/authentication/ISocialAuthNeedsEmailResponse";
import {
  isSocialAuthOtcSentResponse,
  ISocialAuthOtcSentResponse,
} from "../../client/models/authentication/ISocialAuthOtcSentResponse";
import IAuthenticationResponse from "../../client/models/authentication/IAuthenticationResponse";
import {SocialSignInProvider} from "../../client/models/authentication/SocialSignInProvider";
import {logger} from "../log/logger";

export type SocialSignInResult =
  | { kind: "signedIn"; auth: IAuthenticationResponse }
  | {
      kind: "needsEmail";
      linkingToken: string;
      prefillEmail: string | null;
      isPrefillEmailVerified: boolean | null;
    }
  | {
      kind: "otcSent";
      linkingToken: string;
      email: string;
    }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

const genericSignInFailedMessage = "Sign-in failed. Please try again.";

/**
 * RN-only Google/Apple accept-token flow: nonce → native SDK → POST accept.
 * Buttons must gate on isAvailable() so Electron/browser never call this.
 */
export const socialSignInSdk = {
  isAvailable(): boolean {
    return nativeBridge.isRunningInReactNative;
  },

  async signIn(provider: SocialSignInProvider): Promise<SocialSignInResult> {
    if (!socialSignInSdk.isAvailable()) {
      return {kind: "error", message: "Social sign-in is only available in the mobile app."};
    }

    try {
      const nonce = await playgroundApi.socialAuthNonce();
      const outcome = provider === SocialSignInProvider.Google
        ? await nativeBridge.requestGoogleIdToken(nonce)
        : await nativeBridge.requestAppleIdToken(nonce);

      if (outcome.status === "cancelled") {
        return {kind: "cancelled"};
      }
      if (outcome.status === "error") {
        logger.userAuth.error({message: outcome.message || genericSignInFailedMessage});
        return {kind: "error", message: genericSignInFailedMessage};
      }

      const acceptResult = await playgroundApi.socialAuthAccept(
        provider,
        outcome.idToken,
        nonce,
      );

      if (isSocialAuthNeedsEmailResponse(acceptResult)) {
        return mapNeedsEmail(acceptResult);
      }

      if (isSocialAuthOtcSentResponse(acceptResult)) {
        return mapOtcSent(acceptResult);
      }

      await userAuthentication.completeSocialSignIn(acceptResult, provider);
      return {kind: "signedIn", auth: acceptResult};
    } catch (e) {
      const raw = e instanceof Error ? e.message : "";
      logger.userAuth.error({message: raw || genericSignInFailedMessage});
      return {kind: "error", message: genericSignInFailedMessage};
    }
  },
};

function mapPrefillEmailVerified(value: boolean | null | undefined): boolean | null {
  if (value === true) {
    return true;
  }
  if (value === false) {
    return false;
  }
  return null;
}

function mapOtcSent(response: ISocialAuthOtcSentResponse): SocialSignInResult {
  return {
    kind: "otcSent",
    linkingToken: response.linking_token,
    email: response.email,
  };
}

function mapNeedsEmail(response: ISocialAuthNeedsEmailResponse): SocialSignInResult {
  return {
    kind: "needsEmail",
    linkingToken: response.linking_token,
    prefillEmail: response.prefill_email,
    isPrefillEmailVerified: mapPrefillEmailVerified(response.prefill_email_verified),
  };
}
